// Library service — fetches jobs via Cloudflare Workers API (same backend as mobile app)
// Uses cursor-based pagination matching Worker API response format
// Includes polling for active jobs (queued: 5s, generating: 30s)
// API worker checks Cloudflare Workflow instance status on each poll for instant error detection
import { libraryStore } from './library.store.js';
import { authStore } from '@features/auth/auth.store.js';
import { profileStore } from '@features/profile/profile.store.js';
import { jobsApi, normalizeJob } from '@services/api/endpoints/jobs.js';
import { creditsApi } from '@services/api/endpoints/credits.js';

// ─── Polling constants ───
const POLL_INTERVAL_QUEUED = 5000;       // 5 seconds for queued videos
const POLL_INTERVAL_GENERATING = 30000;  // 30 seconds for generating videos (API worker checks workflow status on each poll)
const CLIENT_TIMEOUT_MS = 25 * 60 * 1000; // 25 min safety net
const INITIAL_LIBRARY_FETCH_LIMIT = 30;
const INCREMENTAL_LIBRARY_FETCH_LIMIT = 10;

export const libraryService = {
  // ─── Polling state ───
  _queuedPollTimer: null,
  _generatingPollTimer: null,
  _isPollingQueued: false,
  _isPollingGenerating: false,
  _loadMoreInFlight: false,
  _requestEpoch: 0,

  /**
   * Fetch user's videos from Worker API.
   * Uses cursor-based pagination.
   * @param {string|null} [cursor=null] - Pagination cursor (null = first page)
   */
  _fetchInFlight: false,

  async fetchVideos(cursor = null) {
    const authSnapshot = authStore.get();
    const isAuthenticated = authSnapshot.isAuthenticated;
    const currentUserId = authSnapshot.user?.id || null;
    if (!isAuthenticated) {
      libraryStore.set((prev) => ({
        ...prev,
        videos: [],
        isLoading: false,
        hasLoadedOnce: false,
        error: null,
      }));
      return;
    }

    if (this._fetchInFlight && !cursor) return;
    this._fetchInFlight = true;
    const requestEpoch = this._requestEpoch;

    const current = libraryStore.get();
    if (current.videos.length === 0) {
      libraryStore.setKey('isLoading', true);
    }

    const attempt = async () => {
      const result = await jobsApi.list({
        cursor: cursor || undefined,
        limit: cursor ? INCREMENTAL_LIBRARY_FETCH_LIMIT : INITIAL_LIBRARY_FETCH_LIMIT,
      });
      const latestAuth = authStore.get();
      const latestUserId = latestAuth.user?.id || null;
      const isStale =
        this._requestEpoch !== requestEpoch
        || !latestAuth.isAuthenticated
        || !currentUserId
        || latestUserId !== currentUserId;
      if (isStale) return;
      const normalizedItems = (result.items || []).map(normalizeJob).filter(Boolean);
      libraryStore.set((prev) => ({
        ...prev,
        videos: cursor ? [...prev.videos, ...normalizedItems] : normalizedItems,
        pagination: {
          cursor: result.nextCursor,
          hasMore: !!result.nextCursor,
        },
        isLoading: false,
        hasLoadedOnce: true,
        error: null,
      }));
    };

    try {
      await attempt();
      this.evaluatePolling();
    } catch (firstError) {
      console.warn('[libraryService] fetchVideos first attempt failed:', firstError.message);
      // Retry once after a short delay
      try {
        await new Promise((r) => setTimeout(r, 1500));
        await attempt();
        this.evaluatePolling();
      } catch (retryError) {
        console.error('[libraryService] fetchVideos retry failed:', retryError.message);
        libraryStore.set((prev) => ({
          ...prev,
          videos: prev.videos.length > 0 ? prev.videos : [],
          isLoading: false,
          hasLoadedOnce: true,
          error: retryError.message || 'Failed to load videos',
        }));
      }
    } finally {
      this._fetchInFlight = false;
    }
  },

  // ─── Polling Logic (mirrors mobile LibraryContent.tsx) ───

  /**
   * Get queued videos from store (status === 'queued')
   */
  _getQueuedVideos() {
    const { videos } = libraryStore.get();
    return videos.filter((v) => v.status === 'queued');
  },

  /**
   * Get generating videos from store (status === 'generating')
   */
  _getGeneratingVideos() {
    const { videos } = libraryStore.get();
    return videos.filter((v) => v.status === 'generating');
  },

  /**
   * Poll a list of videos individually via jobsApi.getById()
   * Updates store if status/videoUrl/statusReason changed.
   * @param {Array} videosToPolls
   * @param {string} label - for logging
   */
  async _pollVideos(videosToPolls, label) {
    const now = Date.now();
    if (!authStore.get().isAuthenticated) return;

    for (const video of videosToPolls) {
      if (!authStore.get().isAuthenticated) return;
      // Client-side timeout check (safety net if backend cron fails)
      const createdTime = new Date(video.createdAt).getTime();
      const timeElapsed = now - createdTime;

      if (timeElapsed > CLIENT_TIMEOUT_MS && video.status !== 'failed') {
        console.warn(`[libraryService] Job ${video.id} stuck for ${Math.round(timeElapsed / 60000)} minutes`);
        try {
          await jobsApi.failStuck(video.id);
          this._updateVideoInStore(video.id, { status: 'failed', statusReason: 'client_timeout' });
        } catch (error) {
          console.error(`[libraryService] failStuck error for ${video.id}:`, error);
        }
        continue;
      }

      try {
        const response = await jobsApi.getById(video.id);
        const job = response.job;

        if (!job) continue;

        // Update only if something changed
        if (
          job.status !== video.status ||
          job.videoUrl !== video.videoUrl ||
          job.statusReason !== video.statusReason
        ) {
          console.log(`[libraryService] ${label} poll: ${video.id} ${video.status} → ${job.status}`);
          this._updateVideoInStore(video.id, {
            status: job.status,
            statusReason: job.statusReason || null,
            title: (typeof job.title === 'string' && job.title.trim()) ? job.title.trim() : video.title || 'Video',
            videoUrl: job.videoUrl || null,
            url: job.videoUrl || null,
            thumbnailUrl: job.thumbnailUrl || null,
            thumbnail: job.thumbnailUrl || null,
          });

          // If job just completed or failed, refresh credits
          if (job.status === 'ready' || job.status === 'failed') {
            this._refreshCredits();
          }
        }
      } catch (error) {
        const msg = error?.message || String(error);
        if (msg.includes('not found') || msg.includes('Not found')) {
          console.warn(`[libraryService] Job ${video.id} not found, marking as failed`);
          this._updateVideoInStore(video.id, { status: 'failed' });
        } else {
          console.error(`[libraryService] Poll error for ${video.id}:`, error);
        }
      }
    }

    // Re-evaluate polling after updates
    this.evaluatePolling();
  },

  /**
   * Update a single video in the store (immutable update)
   */
  _updateVideoInStore(videoId, updates) {
    libraryStore.set((prev) => ({
      ...prev,
      videos: prev.videos.map((v) =>
        v.id === videoId ? { ...v, ...updates } : v
      ),
    }));
  },

  /**
   * Refresh credits from backend and update auth store
   */
  async _refreshCredits() {
    try {
      const { balance } = await creditsApi.getBalance();
      authStore.setKey('credits', balance);
      profileStore.set((prev) => ({
        ...prev,
        stats: { ...prev.stats, creditsRemaining: balance },
      }));
    } catch (err) {
      console.warn('[libraryService] _refreshCredits error:', err?.message);
    }
  },

  /**
   * Evaluate whether polling timers should be running.
   * Starts polling if there are active videos, stops if there aren't.
   */
  evaluatePolling() {
    const { isAuthenticated } = authStore.get();
    const queuedVideos = this._getQueuedVideos();
    const generatingVideos = this._getGeneratingVideos();

    // ── Queued polling (fast: every 5s) ──
    if (queuedVideos.length > 0 && isAuthenticated && !this._queuedPollTimer) {
      console.log(`[libraryService] Starting queued polling (${queuedVideos.length} videos)`);
      this._queuedPollTimer = setInterval(() => {
        const current = this._getQueuedVideos();
        if (current.length > 0) {
          this._pollVideos(current, 'queued');
        }
      }, POLL_INTERVAL_QUEUED);
    } else if ((queuedVideos.length === 0 || !isAuthenticated) && this._queuedPollTimer) {
      console.log('[libraryService] Stopping queued polling (no queued videos)');
      clearInterval(this._queuedPollTimer);
      this._queuedPollTimer = null;
    }

    // ── Generating polling (every 15s for fast error/completion detection) ──
    if (generatingVideos.length > 0 && isAuthenticated && !this._generatingPollTimer) {
      console.log(`[libraryService] Starting generating polling (${generatingVideos.length} videos)`);
      // Immediate first poll — don't wait for the interval to fire
      this._pollVideos(generatingVideos, 'generating');
      this._generatingPollTimer = setInterval(() => {
        const current = this._getGeneratingVideos();
        if (current.length > 0) {
          this._pollVideos(current, 'generating');
        }
      }, POLL_INTERVAL_GENERATING);
    } else if ((generatingVideos.length === 0 || !isAuthenticated) && this._generatingPollTimer) {
      console.log('[libraryService] Stopping generating polling (no generating videos)');
      clearInterval(this._generatingPollTimer);
      this._generatingPollTimer = null;
    }
  },

  /**
   * Stop all polling timers (call on logout or unmount)
   */
  stopPolling() {
    this._requestEpoch += 1;
    this._fetchInFlight = false;
    if (this._queuedPollTimer) {
      clearInterval(this._queuedPollTimer);
      this._queuedPollTimer = null;
    }
    if (this._generatingPollTimer) {
      clearInterval(this._generatingPollTimer);
      this._generatingPollTimer = null;
    }
  },

  /**
   * Fetch videos once for the current session if needed.
   */
  ensureVideosLoaded() {
    const state = libraryStore.get();
    if (!authStore.get().isAuthenticated) return;
    if (state.videos.length === 0 && !state.isLoading) {
      this.fetchVideos();
    }
  },

  /**
   * Set filter
   */
  setFilter(filter) {
    libraryStore.setKey('filter', filter);
  },

  /**
   * Load more items (cursor-based)
   */
  async loadMore() {
    const { pagination } = libraryStore.get();
    if (!pagination.hasMore || !pagination.cursor || this._loadMoreInFlight) return;

    this._loadMoreInFlight = true;
    try {
      await this.fetchVideos(pagination.cursor);
    } finally {
      this._loadMoreInFlight = false;
    }
  },

  /**
   * Delete a video/job via Worker API
   */
  async deleteVideo(videoId) {
    try {
      await jobsApi.delete(videoId);

      // Remove from store
      libraryStore.set((prev) => ({
        ...prev,
        videos: prev.videos.filter(v => v.id !== videoId),
      }));
    } catch (error) {
      console.error('[libraryService] deleteVideo error:', error.message);
      throw error;
    }
  },
};
